<script setup lang="ts">
type StudentUserData = App.Auth.Data.StudentUserData;

import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import Dialog from '@/components/dialog/Dialog.vue';
import FormRows from '@/components/formRows/FormRows.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';
import PasswordRequirementsList from '@/components/passwordRequirementsList/PasswordRequirementsList.vue';
import PasswordStrength from '@/components/passwordStrength/PasswordStrength.vue';

import ArrowRight from '@/icons/line/arrow-right.svg';
import Check from '@/icons/line/check.svg';

defineProps<{
  user: StudentUserData;
}>();

const { show, close, closeLocally } = useDialog();
const [isCancelDialogOpen, toggleCancelDialog] = useToggle(false);

const isDialogOpen = computed(() => !isCancelDialogOpen.value && show.value);

const isCurrentPasswordSet = ref(false);
const passwordScore = ref(0);

const isPasswordLongEnough = computed(() =>
  form.fields.new_password ? form.fields.new_password.length >= 8 : false
);
const doPasswordsMatch = computed(
  () =>
    form.fields.new_password &&
    form.fields.new_password.length > 0 &&
    form.fields.new_password === form.fields.new_password_confirmation
);

const form = useForm({
  url: route('student.profile.security.password.update'),
  method: 'PUT',
  fields: {
    password: '',
    new_password: '',
    new_password_confirmation: ''
  },
  hooks: {
    error(errors) {
      if (errors.password) {
        isCurrentPasswordSet.value = false;
      }
    },
    success() {
      close();
    }
  }
});

const forgotPasswordForm = useForm({
  method: 'POST',
  url: route('student.profile.security.password.reset'),
  fields: {}
});

function handleNext() {
  if (form.fields.password && form.fields.password.length > 0) {
    isCurrentPasswordSet.value = true;
    return;
  }

  form.setErrors({
    password: 'Please enter your current password.'
  });
}

function handleScoreChange(score: number) {
  passwordScore.value = score;
}

function handleToggleCancelDialog() {
  if (form.isDirty) {
    toggleCancelDialog();
    return;
  }
  closeLocally();
}

function handleCancelDialogConfirm() {
  toggleCancelDialog();
  close();
}
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/student-parent.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <Dialog
    title="Change Password"
    :isOpen="isDialogOpen"
    hideFooter
    isStacked
    @onClose="handleToggleCancelDialog"
  >
    <template #form>
      <div v-if="!isCurrentPasswordSet" class="flex flex-col gap-y-5" @keyup.enter="handleNext">
        <FormRows>
          <FormTextInput
            :error="form.errors.password"
            :modelValue="form.fields.password"
            helperText="To change your password, please enter your current password."
            label="Current password"
            name="password"
            type="password"
            @update:modelValue="(value) => (form.fields.password = value)"
          />
        </FormRows>
        <div class="flex flex-col gap-y-3">
          <Button
            :color="ButtonColor.primary"
            :iconRight="ArrowRight"
            :isDisabled="!form.fields.password"
            :variant="ButtonVariant.solid"
            :size="ButtonSize.lg"
            @click="handleNext"
          >
            Next
          </Button>
          <Button
            :color="ButtonColor.slate"
            :variant="ButtonVariant.soft"
            :size="ButtonSize.lg"
            @click="forgotPasswordForm.submit"
          >
            Forgot password?
          </Button>
        </div>
      </div>
      <div v-else class="flex flex-col gap-y-5" @keyup.enter="form.submit">
        <div class="flex flex-col gap-y-7">
          <FormRows>
            <FormTextInput
              :error="form.errors.new_password"
              :modelValue="form.fields.new_password"
              label="New password"
              name="new_password"
              type="password"
              @update:modelValue="(value) => (form.fields.new_password = value)"
            >
              <template #helperText>
                <PasswordStrength :password="form.fields.new_password" @score="handleScoreChange" />
              </template>
            </FormTextInput>
            <FormTextInput
              :error="form.errors.new_password_confirmation"
              :isLoading="form.processing"
              :modelValue="form.fields.new_password_confirmation"
              label="Confirm new password"
              name="new_password_confirmation"
              type="password"
              @update:modelValue="(value) => (form.fields.new_password_confirmation = value)"
            />
          </FormRows>

          <PasswordRequirementsList
            :isPasswordLongEnough="isPasswordLongEnough"
            :doPasswordsMatch="!!doPasswordsMatch"
            :passwordScore="passwordScore"
          />
        </div>
        <div class="flex flex-col gap-y-3">
          <Button
            :color="ButtonColor.primary"
            :iconRight="Check"
            :variant="ButtonVariant.solid"
            :size="ButtonSize.lg"
            @click="form.submit"
          >
            Set password
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
  <DangerDialog
    title="Discard Changes?"
    message="Your changes will not be saved."
    :isOpen="isCancelDialogOpen"
    cancelButtonLabel="Stay on this page"
    confirmButtonLabel="Discard changes"
    isStacked
    @onCancel="toggleCancelDialog"
    @onClose="toggleCancelDialog"
    @onConfirm="handleCancelDialogConfirm"
  />
</template>
